import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FileText, Plus, Clock, CheckCircle, XCircle, AlertCircle,
  Search, Calendar, Eye, RefreshCw, X, FolderOpen, Award,
  ChevronLeft, ChevronRight, ArrowRight, ExternalLink, FileImage, File, Maximize2
} from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';
import api from '../../services/apiService';
import SuspendedCompanyOverlay from './shared/SuspendedCompanyOverlay';

// User Avatar component
const UserAvatar = ({ user, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base'
  };

  const profilePicture = user?.user_profile_picture || user?.profile_picture;

  if (profilePicture) {
    return (
      <img
        src={profilePicture}
        alt={user.user_name || user.name}
        className={`${sizeClasses[size]} rounded-xl object-cover border-2 border-orange-100`}
      />
    );
  }

  const name = user?.user_name || user?.name || 'U';
  return (
    <div className={`${sizeClasses[size]} rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-black`}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
};

const WindCompanyNominationsList = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nominations, setNominations] = useState([]);
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Dossier Modal State
  const [showDossier, setShowDossier] = useState(false);
  const [selectedNomination, setSelectedNomination] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [previewExpanded, setPreviewExpanded] = useState(false);

  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    submitted: 0,
    under_review: 0,
    approved: 0,
    rejected: 0
  });

  useEffect(() => {
    fetchNominations();
  }, []);

  // Handle selected nomination from URL parameter (e.g., from dashboard click)
  useEffect(() => {
    const selectedId = searchParams.get('selected');
    if (selectedId && nominations.length > 0 && !showDossier) {
      fetchNominationDetails(parseInt(selectedId));
    }
  }, [nominations, searchParams]);

  const fetchNominations = async () => {
    setLoading(true);
    try {
      const response = await api.get('/oem/nominations/');
      setNominations(response.data);

      const allNominations = response.data;
      setStats({
        total: allNominations.length,
        pending: allNominations.filter(n => n.status === 'pending').length,
        submitted: allNominations.filter(n => n.status === 'submitted').length,
        under_review: allNominations.filter(n => n.status === 'under_review').length,
        approved: allNominations.filter(n => n.status === 'approved').length,
        rejected: allNominations.filter(n => n.status === 'rejected').length
      });
    } catch (err) {
      console.error('Error fetching nominations:', err);
      setError('Failed to load nominations');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: 'Pending', bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
      submitted: { label: 'Submitted', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
      under_review: { label: 'Under Review', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
      approved: { label: 'Approved', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
      rejected: { label: 'Rejected', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
      revoked: { label: 'Revoked', bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
      expired: { label: 'Expired', bg: 'bg-slate-100', text: 'text-slate-500', dot: 'bg-slate-400' },
    };
    return configs[status] || configs.pending;
  };

  // Filter and sort nominations
  const filteredNominations = useMemo(() => {
    let filtered = nominations.filter((nomination) => {
      // Status filter
      if (filterStatus !== 'all') {
        if (filterStatus === 'submitted') {
          if (!['submitted', 'under_review'].includes(nomination.status)) return false;
        } else if (nomination.status !== filterStatus) {
          return false;
        }
      }

      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          nomination.nomination_number?.toLowerCase().includes(term) ||
          nomination.user_name?.toLowerCase().includes(term) ||
          nomination.user_email?.toLowerCase().includes(term) ||
          nomination.authorization_level_name?.toLowerCase().includes(term) ||
          nomination.authorization_level_code?.toLowerCase().includes(term)
        );
      }

      return true;
    });

    return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [nominations, filterStatus, searchTerm]);

  const totalPages = Math.ceil(filteredNominations.length / itemsPerPage) || 1;
  const paginatedNominations = filteredNominations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getVisiblePages = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const statusFilters = [
    { key: 'all', label: 'ALL', count: stats.total },
    { key: 'pending', label: 'PENDING', count: stats.pending },
    { key: 'submitted', label: 'SUBMITTED', count: stats.submitted + stats.under_review },
    { key: 'approved', label: 'APPROVED', count: stats.approved },
    { key: 'rejected', label: 'REJECTED', count: stats.rejected },
  ];

  // Dossier Modal Functions
  const fetchNominationDetails = async (nominationId) => {
    setLoadingDetails(true);
    setShowDossier(true);
    try {
      const response = await api.get(`/oem/nominations/${nominationId}/`);
      setSelectedNomination(response.data);
    } catch (err) {
      console.error('Error fetching nomination details:', err);
      alert('Failed to load nomination details');
      setShowDossier(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeDossier = () => {
    setShowDossier(false);
    setSelectedNomination(null);
    setPreviewDocument(null);
    setPreviewExpanded(false);
    // Clear the selected param from URL
    if (searchParams.has('selected')) {
      searchParams.delete('selected');
      setSearchParams(searchParams);
    }
  };

  // Dossier Modal Component
  const DossierModal = () => {
    if (!showDossier) return null;

    if (loadingDetails || !selectedNomination) {
      return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-slate-600 font-medium">Loading dossier...</span>
            </div>
          </div>
        </div>
      );
    }

    const statusConfig = getStatusConfig(selectedNomination.status);

    // Calculate compliance
    const requiredDocs = selectedNomination.required_documents || [];
    const userQuals = selectedNomination.user_qualifications || [];

    const getDocStatus = (doc) => {
      const userQual = userQuals.find(q => q.id === doc.id);
      if (!userQual) return { status: 'missing', label: 'Missing' };
      if (userQual.is_expired) return { status: 'expired', label: 'Expired' };
      return { status: 'valid', label: 'Valid' };
    };

    const compliantCount = requiredDocs.filter(doc => getDocStatus(doc).status === 'valid').length;
    const compliancePercent = requiredDocs.length > 0 ? Math.round((compliantCount / requiredDocs.length) * 100) : 100;
    const isFullyCompliant = compliancePercent === 100;

    const getFileType = (url) => {
      if (!url) return 'unknown';
      const lower = url.toLowerCase();
      if (lower.includes('.pdf')) return 'pdf';
      if (lower.includes('.png') || lower.includes('.jpg') || lower.includes('.jpeg') || lower.includes('.gif') || lower.includes('.webp')) return 'image';
      return 'unknown';
    };

    const handlePreview = (url, name) => {
      if (!url) return;
      setPreviewDocument({ url, name, type: getFileType(url) });
    };

    const toggleExpandPreview = () => {
      setPreviewExpanded(!previewExpanded);
    };

    const closePreview = () => {
      setPreviewDocument(null);
      setPreviewExpanded(false);
    };

    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={closeDossier}>
        <div className="bg-white rounded-2xl shadow-2xl w-[95vw] max-w-[1400px] max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>

          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                <FolderOpen className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h2 className="text-sm font-black text-slate-900 uppercase tracking-wide">Nomination Dossier</h2>
                <p className="text-xs text-slate-500">{selectedNomination.nomination_number}</p>
              </div>
            </div>
            <button
              onClick={closeDossier}
              className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
            >
              <X className="h-5 w-5 text-slate-400" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 flex overflow-hidden">

            {/* Left Panel - Candidate Info & Document Preview */}
            <div className="w-1/3 border-r border-slate-100 flex flex-col bg-slate-50">

              {/* Candidate Info - shrinks when preview is active */}
              {previewDocument ? (
                // Compact version when previewing document
                <div className="p-3 border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <UserAvatar user={selectedNomination} size="sm" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xs font-black text-slate-900 truncate">{selectedNomination.user_name}</h3>
                      <p className="text-[10px] text-slate-500 truncate">{selectedNomination.company_name}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] font-bold text-slate-400">L{selectedNomination.user_current_level || 0}</span>
                      <ArrowRight className="h-2.5 w-2.5 text-orange-500" />
                      <span className="text-[10px] font-black text-orange-600">L{selectedNomination.authorization_level_code?.replace(/\D/g, '') || '?'}</span>
                    </div>
                    <div className={`px-2 py-1 rounded-lg ${isFullyCompliant ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                      <span className={`text-[10px] font-black ${isFullyCompliant ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {compliancePercent}%
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                // Full version when no preview
                <div className="p-5 border-b border-slate-200">
                  <div className="flex items-center gap-4 mb-4">
                    <UserAvatar user={selectedNomination} size="lg" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-black text-slate-900 truncate">{selectedNomination.user_name}</h3>
                      <p className="text-xs text-slate-500 truncate">{selectedNomination.user_email}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-xl p-3 border border-slate-200">
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Company</p>
                      <p className="text-xs font-bold text-slate-900 truncate">{selectedNomination.company_name}</p>
                    </div>
                    <div className="bg-white rounded-xl p-3 border border-slate-200">
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Target Level</p>
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-bold text-slate-400">L{selectedNomination.user_current_level || 0}</span>
                        <ArrowRight className="h-3 w-3 text-orange-500" />
                        <span className="text-xs font-black text-orange-600">L{selectedNomination.authorization_level_code?.replace(/\D/g, '') || '?'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Compliance Status */}
                  <div className={`mt-4 p-4 rounded-xl border ${isFullyCompliant ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-black uppercase tracking-wide ${isFullyCompliant ? 'text-emerald-700' : 'text-amber-700'}`}>
                        Compliance Status
                      </span>
                      <span className={`text-lg font-black ${isFullyCompliant ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {compliancePercent}%
                      </span>
                    </div>
                    <div className="w-full h-2 bg-white rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${isFullyCompliant ? 'bg-emerald-500' : 'bg-amber-500'}`}
                        style={{ width: `${compliancePercent}%` }}
                      />
                    </div>
                    <p className={`mt-2 text-[10px] font-bold ${isFullyCompliant ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {compliantCount} of {requiredDocs.length} requirements met
                    </p>
                  </div>
                </div>
              )}

              {/* Document Preview Area */}
              <div className="flex-1 p-4 overflow-hidden flex flex-col">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3">Document Preview</p>
                <div className="flex-1 bg-white rounded-xl border border-slate-200 overflow-hidden">
                  {previewDocument ? (
                    <div className="h-full flex flex-col">
                      <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                        <span className="text-xs font-bold text-slate-700 truncate">{previewDocument.name}</span>
                        <div className="flex items-center gap-1">
                          <a
                            href={previewDocument.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 hover:bg-slate-200 rounded transition-colors"
                          >
                            <ExternalLink className="h-3 w-3 text-slate-500" />
                          </a>
                          <button
                            onClick={closePreview}
                            className="p-1 hover:bg-slate-200 rounded transition-colors"
                          >
                            <X className="h-3 w-3 text-slate-500" />
                          </button>
                        </div>
                      </div>
                      <div className="flex-1 overflow-auto p-2 bg-slate-100 relative">
                        {previewDocument.type === 'pdf' ? (
                          <iframe
                            src={previewDocument.url}
                            className="w-full h-full min-h-[200px] rounded bg-white"
                            title="Document Preview"
                          />
                        ) : previewDocument.type === 'image' ? (
                          <div className="flex items-center justify-center h-full">
                            <img
                              src={previewDocument.url}
                              alt={previewDocument.name}
                              className="max-w-full max-h-full object-contain rounded shadow-lg"
                            />
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full text-slate-400">
                            <File className="h-12 w-12 mb-2" />
                            <p className="text-xs">Preview not available</p>
                          </div>
                        )}
                        {/* Expand button */}
                        <button
                          onClick={toggleExpandPreview}
                          className="absolute bottom-4 right-4 p-2 bg-orange-500 text-white rounded-lg shadow-lg hover:bg-orange-600 transition-colors"
                          title="Expand preview"
                        >
                          <Maximize2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-slate-400 p-6">
                      <FileImage className="h-12 w-12 mb-3" />
                      <p className="text-xs font-medium text-center">Select a document to preview</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Panel - Requirements & Documents */}
            <div className="flex-1 flex flex-col overflow-hidden bg-white">

              {/* Requirements Checklist */}
              <div className="p-5 border-b border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[10px] font-black text-slate-900 uppercase tracking-widest">Requirements Checklist</h3>
                  <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase ${statusConfig.bg} ${statusConfig.text}`}>
                    {statusConfig.label}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {requiredDocs.map((doc) => {
                    const docStatus = getDocStatus(doc);
                    const userQual = userQuals.find(q => q.id === doc.id);

                    return (
                      <div
                        key={doc.id}
                        className={`p-3 rounded-xl border cursor-pointer transition-all hover:shadow-md ${
                          docStatus.status === 'valid'
                            ? 'bg-emerald-50 border-emerald-200'
                            : docStatus.status === 'expired'
                            ? 'bg-red-50 border-red-200'
                            : 'bg-amber-50 border-amber-200'
                        }`}
                        onClick={() => userQual?.certificate_file && handlePreview(userQual.certificate_file, doc.name)}
                      >
                        <div className="flex items-start gap-2">
                          {docStatus.status === 'valid' ? (
                            <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                          ) : docStatus.status === 'expired' ? (
                            <Clock className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-bold truncate ${
                              docStatus.status === 'valid' ? 'text-emerald-900'
                                : docStatus.status === 'expired' ? 'text-red-900'
                                : 'text-amber-900'
                            }`}>
                              {doc.name}
                            </p>
                            <p className={`text-[10px] ${
                              docStatus.status === 'valid' ? 'text-emerald-600'
                                : docStatus.status === 'expired' ? 'text-red-600'
                                : 'text-amber-600'
                            }`}>
                              {docStatus.label}
                              {userQual?.expiry_date && ` • Exp: ${formatDate(userQual.expiry_date)}`}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Complete Document Ledger */}
              <div className="flex-1 overflow-hidden flex flex-col p-5">
                <h3 className="text-[10px] font-black text-slate-900 uppercase tracking-widest mb-4">Complete Document Ledger</h3>

                <div className="flex-1 overflow-auto border border-slate-200 rounded-xl">
                  <table className="w-full">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-[9px] font-black text-slate-500 uppercase tracking-widest">Document</th>
                        <th className="px-4 py-3 text-left text-[9px] font-black text-slate-500 uppercase tracking-widest">Certificate #</th>
                        <th className="px-4 py-3 text-left text-[9px] font-black text-slate-500 uppercase tracking-widest">Expiry Date</th>
                        <th className="px-4 py-3 text-left text-[9px] font-black text-slate-500 uppercase tracking-widest">Status</th>
                        <th className="px-4 py-3 text-right text-[9px] font-black text-slate-500 uppercase tracking-widest">View</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {userQuals.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-4 py-8 text-center">
                            <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                            <p className="text-xs text-slate-500">No documents uploaded</p>
                          </td>
                        </tr>
                      ) : (
                        userQuals.map((qual, index) => (
                          <tr key={`${qual.id}-${index}`} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-slate-400" />
                                <span className="text-xs font-bold text-slate-900">{qual.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-xs text-slate-600 font-mono">
                                {qual.certificate_number || '—'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-xs font-medium ${qual.is_expired ? 'text-red-600' : 'text-slate-600'}`}>
                                {formatDate(qual.expiry_date)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${
                                qual.is_expired
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-emerald-100 text-emerald-700'
                              }`}>
                                {qual.is_expired ? 'Expired' : 'Valid'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              {qual.certificate_file ? (
                                <button
                                  onClick={() => handlePreview(qual.certificate_file, qual.name)}
                                  className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all ${
                                    previewDocument?.url === qual.certificate_file
                                      ? 'bg-orange-500 text-white'
                                      : 'bg-slate-100 text-slate-600 hover:bg-orange-100 hover:text-orange-600'
                                  }`}
                                >
                                  View
                                </button>
                              ) : (
                                <span className="text-[10px] text-slate-400">No file</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50">
            <div className="flex items-center gap-3">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Created:</span>
              <span className="text-xs font-bold text-slate-700">{formatDate(selectedNomination.created_at)}</span>
              <span className="text-slate-300">•</span>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Validity:</span>
              <span className="text-xs font-bold text-slate-700">
                {formatDate(selectedNomination.date_start)} → {formatDate(selectedNomination.date_end)}
              </span>
            </div>
            <button
              onClick={closeDossier}
              className="h-10 px-5 bg-slate-200 text-slate-700 rounded-xl hover:bg-slate-300 transition-colors text-[10px] font-black uppercase tracking-wide"
            >
              Close
            </button>
          </div>

          {/* Expanded Preview Overlay */}
          {previewExpanded && previewDocument && (
            <div
              className="absolute inset-0 bg-black/80 flex items-center justify-center z-60"
              onClick={() => setPreviewExpanded(false)}
            >
              <div
                className="bg-white rounded-2xl shadow-2xl w-[90%] h-[90%] max-w-[1200px] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Expanded Preview Header */}
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-white">
                  <div className="flex items-center gap-3">
                    {previewDocument.type === 'pdf' ? (
                      <File className="h-6 w-6 text-red-500" />
                    ) : (
                      <FileImage className="h-6 w-6 text-blue-500" />
                    )}
                    <div>
                      <h3 className="text-sm font-black text-slate-900">{previewDocument.name}</h3>
                      <p className="text-[10px] text-slate-500">{selectedNomination.user_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={previewDocument.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-9 px-4 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-xs font-bold flex items-center gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Open in new tab
                    </a>
                    <button
                      onClick={() => setPreviewExpanded(false)}
                      className="h-9 w-9 bg-slate-100 text-slate-500 rounded-lg hover:bg-slate-200 hover:text-slate-700 transition-colors flex items-center justify-center"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Expanded Preview Content */}
                <div className="flex-1 overflow-auto p-6 bg-slate-100">
                  {previewDocument.type === 'pdf' ? (
                    <iframe
                      src={previewDocument.url}
                      className="w-full h-full min-h-[600px] rounded-xl bg-white shadow-lg"
                      title="Document Preview"
                    />
                  ) : previewDocument.type === 'image' ? (
                    <div className="flex items-center justify-center h-full">
                      <img
                        src={previewDocument.url}
                        alt={previewDocument.name}
                        className="max-w-full max-h-full object-contain rounded-xl shadow-lg"
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400">
                      <File className="h-20 w-20 mb-4" />
                      <p className="text-lg font-medium">Preview not available for this file type</p>
                      <a
                        href={previewDocument.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-6 px-6 py-3 bg-orange-500 text-white rounded-xl font-bold hover:bg-orange-600 transition-colors"
                      >
                        Download File
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm border border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-600 text-sm font-medium">Loading nominations...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 max-w-md mx-auto text-center">
          <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-7 w-7 text-red-500" />
          </div>
          <h2 className="text-sm font-black text-slate-900 mb-2 uppercase tracking-tight">Error</h2>
          <p className="text-xs text-slate-500 mb-4">{error}</p>
          <button
            onClick={fetchNominations}
            className="text-[10px] font-black text-orange-500 hover:text-orange-600 uppercase tracking-wide"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <SuspendedCompanyOverlay>
      <div className="p-6 max-w-7xl mx-auto space-y-5">
        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total</p>
                <p className="text-2xl font-black text-slate-900 mt-1">{stats.total}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-slate-500" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Pending</p>
                <p className="text-2xl font-black text-amber-600 mt-1">{stats.submitted + stats.under_review}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Approved</p>
                <p className="text-2xl font-black text-emerald-600 mt-1">{stats.approved}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Rejected</p>
                <p className="text-2xl font-black text-red-600 mt-1">{stats.rejected}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Header Card with Filters */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center">
                  <FileText className="h-4 w-4 text-orange-500" />
                </div>
                <div>
                  <h1 className="text-base font-black text-slate-900 uppercase tracking-tight">Nominations</h1>
                  <p className="text-[10px] text-slate-500 font-medium">Manage authorization nominations</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchNominations}
                  className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className="h-4 w-4 text-slate-600" />
                </button>
                <button
                  onClick={() => navigate('/oem-company/nominations/create')}
                  className="h-9 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 flex items-center gap-1.5 font-black uppercase tracking-wide text-[10px] transition-colors shadow-lg shadow-orange-500/20"
                >
                  <Plus className="h-3.5 w-3.5" />
                  New Nomination
                </button>
              </div>
            </div>

            {/* Filters Row */}
            <div className="flex items-center gap-3 mt-4">
              {/* Status Filter Tabs */}
              <div className="flex items-center gap-1.5">
                {statusFilters.map(filter => (
                  <button
                    key={filter.key}
                    onClick={() => { setFilterStatus(filter.key); setCurrentPage(1); }}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wide transition-all ${
                      filterStatus === filter.key
                        ? 'bg-orange-500 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {filter.label}
                    <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[9px] ${
                      filterStatus === filter.key ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-500'
                    }`}>
                      {filter.count}
                    </span>
                  </button>
                ))}
              </div>

              <div className="flex-1" />

              {/* Search */}
              <div className="relative">
                <Search className="h-3.5 w-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search nominations..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="h-9 pl-9 pr-3 w-64 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all text-xs"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-5 py-4 text-left text-[10px] font-black text-slate-600 uppercase tracking-wider">Technician</th>
                  <th className="px-5 py-4 text-left text-[10px] font-black text-slate-600 uppercase tracking-wider">Nomination #</th>
                  <th className="px-5 py-4 text-left text-[10px] font-black text-slate-600 uppercase tracking-wider">Target Level</th>
                  <th className="px-5 py-4 text-left text-[10px] font-black text-slate-600 uppercase tracking-wider">Validity Period</th>
                  <th className="px-5 py-4 text-left text-[10px] font-black text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="px-5 py-4 text-right text-[10px] font-black text-slate-600 uppercase tracking-wider">View Dossier</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedNominations.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-5 py-16 text-center">
                      <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <FileText className="h-8 w-8 text-slate-400" />
                      </div>
                      <h3 className="text-sm font-black text-slate-900 mb-2 uppercase tracking-tight">No nominations found</h3>
                      <p className="text-xs text-slate-500 mb-6 max-w-sm mx-auto">
                        {nominations.length === 0
                          ? "You haven't created any nominations yet."
                          : "No nominations match your current filters."}
                      </p>
                      {nominations.length === 0 && (
                        <button
                          onClick={() => navigate('/oem-company/nominations/create')}
                          className="h-9 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 flex items-center gap-1.5 font-black uppercase tracking-wide text-[10px] transition-colors mx-auto shadow-lg shadow-orange-500/20"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Create First Nomination
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  paginatedNominations.map((nomination) => {
                    const statusConfig = getStatusConfig(nomination.status);
                    const currentLevel = nomination.user_current_level || 0;
                    const targetLevel = nomination.authorization_level_code?.replace(/\D/g, '') || '?';

                    return (
                      <tr key={nomination.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <UserAvatar user={nomination} size="sm" />
                            <div>
                              <p className="text-sm font-black text-slate-900">{nomination.user_name}</p>
                              <p className="text-[10px] text-slate-500 font-medium">{nomination.user_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className="font-mono text-[10px] text-slate-600 font-medium">{nomination.nomination_number}</span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-400">L{currentLevel}</span>
                            <ArrowRight className="h-3.5 w-3.5 text-orange-500" />
                            <span
                              className="px-2 py-0.5 text-[9px] font-black rounded-md text-white uppercase"
                              style={{ backgroundColor: nomination.authorization_level_color || '#F97316' }}
                            >
                              {nomination.authorization_level_code}
                            </span>
                          </div>
                          <p className="text-[10px] text-slate-400 mt-0.5">{nomination.authorization_level_name}</p>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-[10px] text-slate-600 font-medium">
                            {formatDate(nomination.date_start)} → {formatDate(nomination.date_end)}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase ${statusConfig.bg} ${statusConfig.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`} />
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button
                            onClick={() => fetchNominationDetails(nomination.id)}
                            className="px-4 py-2 bg-slate-900 text-white rounded-lg text-[10px] font-bold uppercase tracking-wide hover:bg-slate-800 transition-colors inline-flex items-center gap-2"
                          >
                            <FolderOpen className="h-3 w-3" />
                            Docs
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredNominations.length > 0 && (
            <div className="flex items-center justify-between px-5 py-4 border-t border-slate-100 bg-slate-50">
              <div className="flex items-center gap-4">
                <span className="text-[10px] text-slate-500 font-medium uppercase tracking-wide">
                  Showing <span className="font-black text-slate-700">{(currentPage - 1) * itemsPerPage + 1}</span>-<span className="font-black text-slate-700">{Math.min(currentPage * itemsPerPage, filteredNominations.length)}</span> of <span className="font-black text-slate-700">{filteredNominations.length}</span>
                </span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  className="h-8 px-2 text-xs border border-slate-200 rounded-lg bg-white"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <div className="flex gap-1">
                {currentPage > 1 && (
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className="h-8 px-2 border border-slate-200 rounded-lg hover:bg-white flex items-center transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                )}
                {getVisiblePages().map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`h-8 px-3 rounded-lg text-xs font-bold transition-colors ${
                      page === currentPage
                        ? 'bg-orange-500 text-white'
                        : 'border border-slate-200 hover:bg-white'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                {currentPage < totalPages && (
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className="h-8 px-2 border border-slate-200 rounded-lg hover:bg-white flex items-center transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Dossier Modal */}
        <DossierModal />
      </div>
    </SuspendedCompanyOverlay>
  );
};

export default WindCompanyNominationsList;
